import { z } from 'zod';
import { db, type Asset, type AssetKind } from '../codexDB';
import type { Entity, Link } from '@/types/entity';
import type { Project } from '@/types/project';
import { entitySchema, linkSchema, projectSchema } from '@/lib/schemas/entity.schema';
import { checksumOf, stableStringify } from '@/lib/hash';
import { SCHEMA_VERSION, APP_VERSION, PROJECT_ID } from '@/lib/constants';

/**
 * 내보내기 번들 (02 §6) — 캐논 데이터(project·entities·links)와 사용자 시각 에셋. checksum으로 무결성 확인.
 * 의도적 제외: API 키, `orchestrationRuns`(WS2 작업 로그).
 * Blob 에셋은 base64로 직렬화해 로컬-퍼스트 백업/복원에 포함한다.
 */
export interface ExportedAsset {
  id: string;
  entityId?: string;
  kind: AssetKind;
  mime: string;
  name?: string;
  createdAt: number;
  dataBase64: string;
}

export interface CodexExportBundle {
  schemaVersion: number;
  appVersion: string;
  exportedAt: number;
  checksum: string;
  project: Project;
  entities: Entity[];
  links: Link[];
  assets: ExportedAsset[];
}

const byId = <T extends { id: string }>(a: T, b: T) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
}

function base64ToBlob(dataBase64: string, mime: string): Blob {
  const binary = atob(dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime || 'application/octet-stream' });
}

async function blobToBytes(blob: Blob): Promise<Uint8Array> {
  if (typeof blob.arrayBuffer === 'function') return new Uint8Array(await blob.arrayBuffer());

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('에셋 Blob을 읽지 못했습니다'));
    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        resolve(new Uint8Array(reader.result));
        return;
      }
      reject(new Error('에셋 Blob 읽기 결과가 ArrayBuffer가 아닙니다'));
    };
    reader.readAsArrayBuffer(blob);
  });
}

async function exportAsset(asset: Asset): Promise<ExportedAsset> {
  const dataBase64 = bytesToBase64(await blobToBytes(asset.blob));
  return {
    id: asset.id,
    ...(asset.entityId ? { entityId: asset.entityId } : {}),
    kind: asset.kind,
    mime: asset.mime || asset.blob.type || 'application/octet-stream',
    ...(asset.name ? { name: asset.name } : {}),
    createdAt: asset.createdAt,
    dataBase64,
  };
}

function importAsset(asset: ExportedAsset): Asset {
  return {
    id: asset.id,
    entityId: asset.entityId,
    kind: asset.kind,
    blob: base64ToBlob(asset.dataBase64, asset.mime),
    mime: asset.mime,
    name: asset.name,
    createdAt: asset.createdAt,
  };
}

export async function buildExportBundle(): Promise<CodexExportBundle> {
  const [project, entities, links, assetRows] = await Promise.all([
    db.project.get(PROJECT_ID),
    db.entities.toArray(),
    db.links.toArray(),
    db.assets.toArray(),
  ]);
  if (!project) throw new Error('내보낼 프로젝트가 없습니다');
  const e = entities.slice().sort(byId);
  const l = links.slice().sort(byId);
  const assets = (await Promise.all(assetRows.slice().sort(byId).map(exportAsset))).sort(byId);
  const payload = { project, entities: e, links: l, assets };
  return {
    schemaVersion: SCHEMA_VERSION,
    appVersion: APP_VERSION,
    exportedAt: Date.now(),
    checksum: checksumOf(payload),
    ...payload,
  };
}

const assetKindSchema: z.ZodType<AssetKind> = z.enum([
  'portrait',
  'planet',
  'sigil',
  'location',
  'nebula',
  'cover',
  'city',
  'city-depth',
]);

const exportedAssetSchema: z.ZodType<ExportedAsset> = z.object({
  id: z.string(),
  entityId: z.string().optional(),
  kind: assetKindSchema,
  mime: z.string(),
  name: z.string().optional(),
  createdAt: z.number(),
  dataBase64: z.string(),
});

export const exportBundleSchema = z.object({
  schemaVersion: z.number(),
  appVersion: z.string(),
  exportedAt: z.number(),
  checksum: z.string(),
  project: projectSchema,
  entities: z.array(entitySchema),
  links: z.array(linkSchema),
  assets: z.array(exportedAssetSchema).default([]),
});

export interface ParsedBundle {
  bundle: CodexExportBundle;
  checksumOk: boolean;
  assetsIncluded: boolean;
}

/** zod 검증 + checksum 재계산. 손상 JSON이면 throw → 기존 DB 무손상. */
export function parseBundle(json: unknown): ParsedBundle {
  const hadAssetsField = !!json && typeof json === 'object' && !Array.isArray(json) && Object.prototype.hasOwnProperty.call(json, 'assets');
  const parsed = exportBundleSchema.parse(json) as unknown as CodexExportBundle;
  const entities = parsed.entities.slice().sort(byId);
  const links = parsed.links.slice().sort(byId);
  const assets = (parsed.assets ?? []).slice().sort(byId);
  const normalized = { ...parsed, entities, links, assets };
  const checksumPayload = {
    project: parsed.project,
    entities,
    links,
    assets,
  };
  const legacyChecksumPayload = {
    project: parsed.project,
    entities,
    links,
  };
  const recomputed = checksumOf(checksumPayload);
  const recomputedLegacy = hadAssetsField ? null : checksumOf(legacyChecksumPayload);
  return {
    bundle: normalized,
    checksumOk: recomputed === parsed.checksum || recomputedLegacy === parsed.checksum,
    assetsIncluded: hadAssetsField,
  };
}

export interface BundleIntegrity {
  errors: string[];
}

/** import 전 구조 무결성 검증 — 고아 링크/중복 id/미지원 스키마 차단용 (P0 정규화 무결성) */
export function validateBundleIntegrity(bundle: CodexExportBundle): BundleIntegrity {
  const errors: string[] = [];
  if (bundle.schemaVersion > SCHEMA_VERSION) {
    errors.push(`지원하지 않는 스키마 버전 ${bundle.schemaVersion} (현재 ${SCHEMA_VERSION})`);
  }
  const eIds = new Set<string>();
  for (const e of bundle.entities) {
    if (eIds.has(e.id)) errors.push(`중복 엔티티 id: ${e.id}`);
    eIds.add(e.id);
  }
  const lIds = new Set<string>();
  let orphan = 0;
  for (const l of bundle.links) {
    if (lIds.has(l.id)) errors.push(`중복 관계 id: ${l.id}`);
    lIds.add(l.id);
    if (!eIds.has(l.from) || !eIds.has(l.to)) orphan++;
  }
  if (orphan > 0) errors.push(`고아 링크 ${orphan}건 — 존재하지 않는 엔티티를 참조`);
  const aIds = new Set<string>();
  let orphanAssets = 0;
  for (const a of bundle.assets ?? []) {
    if (aIds.has(a.id)) errors.push(`중복 에셋 id: ${a.id}`);
    aIds.add(a.id);
    if (a.entityId && !eIds.has(a.entityId)) orphanAssets++;
  }
  if (orphanAssets > 0) errors.push(`고아 에셋 ${orphanAssets}건 — 존재하지 않는 엔티티를 참조`);
  return { errors };
}

export interface ImportDiff {
  added: { entities: number; links: number; assets: number };
  changed: { entities: number; links: number; assets: number };
  removed: { entities: number; links: number; assets: number }; // replace 모드에서 사라지는 것
  total: { incoming: number; current: number };
}

/** dry-run 미리보기 (M7 DiffView가 사용) */
export async function diffBundle(
  bundle: CodexExportBundle,
  mode: 'replace' | 'merge',
): Promise<ImportDiff> {
  const [curE, curL, curAssetRows] = await Promise.all([db.entities.toArray(), db.links.toArray(), db.assets.toArray()]);
  const curAssets = await Promise.all(curAssetRows.slice().sort(byId).map(exportAsset));
  const incomingAssets = (bundle.assets ?? []).slice().sort(byId);
  const curEMap = new Map(curE.map((e) => [e.id, e]));
  const curLMap = new Map(curL.map((l) => [l.id, l]));
  const curAMap = new Map(curAssets.map((a) => [a.id, a]));

  let addedE = 0,
    changedE = 0;
  for (const e of bundle.entities) {
    const cur = curEMap.get(e.id);
    if (!cur) addedE++;
    else if (stableStringify(cur) !== stableStringify(e)) changedE++;
  }
  let addedL = 0,
    changedL = 0;
  for (const l of bundle.links) {
    const cur = curLMap.get(l.id);
    if (!cur) addedL++;
    else if (stableStringify(cur) !== stableStringify(l)) changedL++;
  }
  let addedA = 0,
    changedA = 0;
  for (const a of incomingAssets) {
    const cur = curAMap.get(a.id);
    if (!cur) addedA++;
    else if (stableStringify(cur) !== stableStringify(a)) changedA++;
  }
  const inE = new Set(bundle.entities.map((e) => e.id));
  const inL = new Set(bundle.links.map((l) => l.id));
  const inA = new Set(incomingAssets.map((a) => a.id));
  return {
    added: { entities: addedE, links: addedL, assets: addedA },
    changed: { entities: changedE, links: changedL, assets: changedA },
    removed: {
      entities: mode === 'replace' ? curE.filter((e) => !inE.has(e.id)).length : 0,
      links: mode === 'replace' ? curL.filter((l) => !inL.has(l.id)).length : 0,
      assets: mode === 'replace' ? curAssets.filter((a) => !inA.has(a.id)).length : 0,
    },
    total: {
      incoming: bundle.entities.length + bundle.links.length + incomingAssets.length,
      current: curE.length + curL.length + curAssets.length,
    },
  };
}

export async function importBundle(
  bundle: CodexExportBundle,
  mode: 'replace' | 'merge',
  options: { assetsIncluded?: boolean } = {},
): Promise<void> {
  const assetsIncluded = options.assetsIncluded ?? true;
  const assets = (bundle.assets ?? []).map(importAsset);
  await db.transaction('rw', db.entities, db.links, db.project, db.assets, async () => {
    if (mode === 'replace') {
      await Promise.all([
        db.entities.clear(),
        db.links.clear(),
        ...(assetsIncluded ? [db.assets.clear()] : []),
      ]);
    }
    await db.entities.bulkPut(bundle.entities);
    await db.links.bulkPut(bundle.links);
    if (assetsIncluded && assets.length) await db.assets.bulkPut(assets);
    await db.project.put(bundle.project);
  });
}
